import { Button } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { TieredMenu } from 'primereact/tieredmenu';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import { TableDataElement } from './DiagnosticReportStudentsTable';
import { resetDiagnosticMutation } from '../../graphQLOperations/Students';
import { StudentsResetDiagnosticMutation } from '../../graphQLOperations/__generated__/StudentsResetDiagnosticMutation.graphql';
import { useMutation } from 'react-relay';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { buildResetSummary } from '../../businessLogicService/diagnosticReset';
import { DiagnosticResetProgress } from './DiagnosticResetProgress';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { ChevronDown } from 'lucide-react';

interface Props {
  setActiveSelection: Dispatch<SetStateAction<boolean>>;
  activeSelection: boolean;
  selectedStudents: TableDataElement[];
  setSelectedStudents: Dispatch<SetStateAction<TableDataElement[]>>;
  setRefetchKey: Dispatch<SetStateAction<number>>;
  allStudentsClass: TableDataElement[];
}

interface ResetProgress {
  total: number;
  completed: number;
  failed: number;
}

export const DiagnosticReportResetButton: FC<Props> = ({
  setActiveSelection,
  activeSelection,
  selectedStudents,
  setSelectedStudents,
  setRefetchKey,
  allStudentsClass,
}) => {
  const [resetDiagnostic] = useMutation<StudentsResetDiagnosticMutation>(resetDiagnosticMutation);
  const toast = useRef<Toast>(null);
  // null = idle (show the confirm body); set = a reset is running (show the progress body).
  const [resetProgress, setResetProgress] = useState<ResetProgress | null>(null);
  const isResetting = resetProgress !== null;
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; mode?: 'class' | 'custom' | null }>({
    visible: false,
    mode: null,
  });

  // The counts are threaded through the recursion as `processed` rather than read
  // back from state: each recursive call reuses the same closure, so reading
  // `resetProgress` here would be stale. State only mirrors the counts to drive the bar.
  const resetManyStudentDg = (
    students: TableDataElement[],
    total: number,
    processed: { completed: number; failed: number } = { completed: 0, failed: 0 },
  ) => {
    if (students.length === 0) {
      toast.current?.show(buildResetSummary(processed.completed, processed.failed, total));
      setSelectedStudents([]);
      setActiveSelection(false);
      setConfirmModal({ visible: false, mode: null });
      setResetProgress(null);
      return setRefetchKey(Date.now());
    }
    const last = students[students.length - 1];
    // A failed student is counted and skipped so the loop never stalls.
    const advance = (outcome: 'completed' | 'failed') => () => {
      const next = {
        completed: processed.completed + (outcome === 'completed' ? 1 : 0),
        failed: processed.failed + (outcome === 'failed' ? 1 : 0),
      };
      setResetProgress({ total, ...next });
      resetManyStudentDg(students.slice(0, -1), total, next);
    };
    resetDiagnostic({
      variables: {
        studentId: `StudentNode:${splitId(last.id)}`,
        diagnosticId: `StudentSkillDiagnosticNode:${last.lastDiagnosticId}`,
      },
      onCompleted: advance('completed'),
      onError: advance('failed'),
    });
  };

  const btnRef = useRef<TieredMenu>(null);
  const optionsMenu: MenuItem[] = [
    {
      id: '1',
      label: 'Individual students',
      command: () => {
        setActiveSelection(prev => !prev);
      },
    },
    {
      id: '2',
      label: 'Whole class',
      command: () => {
        setConfirmModal({ visible: true, mode: 'class' });
      },
    },
  ];

  const handleReset = () => {
    const students =
      confirmModal.mode === 'class'
        ? allStudentsClass.filter(st => st.lastDiagnosticId && !st.retakeAssigned)
        : selectedStudents;
    if (students.length === 0) return;
    setResetProgress({ total: students.length, completed: 0, failed: 0 });
    resetManyStudentDg(students, students.length);
  };

  const handleCancel = () => {
    setSelectedStudents([]);
    setActiveSelection(false);
    setConfirmModal({ visible: false, mode: null });
  };

  return (
    <>
      {activeSelection && <Button outlined label="Cancel" className="!tw-pr-[21px]" onClick={handleCancel} />}
      <TieredMenu model={optionsMenu} popup ref={btnRef} breakpoint="767px" />
      <Button
        id="dgReset"
        className="tw-flex tw-flex-row tw-gap-x-2 tw-flex-row-reverse md:tw-ml-2 tw-w-1/2"
        icon={<ChevronDown size={20} />}
        disabled={
          (activeSelection && selectedStudents.length === 0) ||
          !allStudentsClass.some(st => st.lastDiagnosticId && !st.retakeAssigned)
        }
        outlined={!activeSelection}
        label={`${activeSelection ? 'Confirm diagnostic reset' : 'Reset students diagnostic'} ${
          selectedStudents.length > 0 ? ` ( ${selectedStudents.length} )` : ''
        }`}
        onClick={e => {
          if (activeSelection) {
            setConfirmModal({ visible: true });
          } else {
            btnRef.current?.toggle(e);
          }
        }}
      />
      <Dialog
        className="col-4"
        showHeader={false}
        closeOnEscape={!isResetting}
        footer={
          isResetting ? null : (
            <div className="flex justify-content-end w-full">
              <Button className="inline" outlined label="Cancel" onClick={handleCancel} />
              <Button
                id="dgConfirmReset"
                className="inline"
                onClick={() => {
                  handleReset();
                }}
                label="Reset diagnostic"
              />
            </div>
          )
        }
        visible={confirmModal.visible}
        onHide={() => {
          if (!isResetting) setConfirmModal({ visible: false, mode: null });
        }}
      >
        {resetProgress ? (
          <DiagnosticResetProgress {...resetProgress} />
        ) : (
          <div className="text-center">
            <div>
              <i className="icon-sm nc_icon-nc_review font-semibold" />
            </div>
            {confirmModal.mode === 'class' ? (
              <>
                <h3 className="font-semibold">Reset diagnostic for the entire class?</h3>
                <p>This will erase their previous diagnostic results.</p>
              </>
            ) : (
              <>
                <h3 className="font-semibold">Reset diagnostics for {selectedStudents.length} students?</h3>
                <p>
                  This will erase the previous diagnostic results for
                  {/* Be careful with the whitespace here it is carefully set to prevent words running into each other */}
                  {selectedStudents.map((st, index) => (
                    <>
                      {' '}
                      <MaskedStudentData student={st} type={PiiTypes.FullName}>
                        {st.name}
                      </MaskedStudentData>
                      {index < selectedStudents.length - 1 ? ',' : ''}
                    </>
                  ))}
                </p>
              </>
            )}
          </div>
        )}
      </Dialog>
      <Toast ref={toast} />
    </>
  );
};
